/**
 * Helpers for the Schedule block overrides (Schedule.jsx / DaySchedule.jsx).
 *
 * These implement, for the schedule block of
 * @plone-collective/volto-techevent:
 *
 * 1. Sticky day tab: the user's explicit day choice is persisted in
 *    localStorage, namespaced by page path.
 * 2. Auto day: on conference days the schedule opens on today's tab,
 *    unless the stored choice was made earlier *today* (a choice from
 *    yesterday must not hijack this morning's visit).
 * 3. Auto scroll: on today's tab the page scrolls so the next upcoming
 *    slot's title is visible below the sticky site header.
 *
 * Everything here is pure and unit-tested in scheduleUtils.test.js.
 * Browser APIs (window/localStorage) are passed in by the caller, so the
 * module is safe to import during SSR.
 */

export const STORAGE_KEY_PREFIX = 'schedule.activeDay:';

/**
 * Pixels above the scroll target: rough height of the sticky site header
 * plus about one timeslot of context, so the visitor sees what is
 * happening now *and* the title of what is next.
 */
export const SCROLL_OFFSET_PX = 140;

/**
 * Format a Date as zero-padded local YYYY-MM-DD (matching the day ids
 * used by the schedule backend).
 */
export function localDayId(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function storageKeyForPath(pathname) {
  return `${STORAGE_KEY_PREFIX}${pathname}`;
}

/**
 * Read a stored day selection. Returns { dayId, savedOn } or null for
 * missing/corrupt/unavailable storage.
 */
export function readStoredDay(key, storage) {
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.dayId === 'string' &&
      typeof parsed.savedOn === 'string'
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Persist a day selection. `today` is the local day id of the moment the
 * choice was made — it powers the "today wins next morning" rule.
 * Never throws (storage may be unavailable in privacy modes).
 */
export function writeStoredDay(key, dayId, today, storage) {
  try {
    storage.setItem(key, JSON.stringify({ dayId, savedOn: today }));
  } catch {
    // Storage unavailable — stickiness degrades gracefully.
  }
}

/**
 * Decide which day tab to activate.
 *
 * @param {string[]} days - day ids (YYYY-MM-DD) in tab order
 * @param {string} today - local day id of "now"
 * @param {{dayId: string, savedOn: string} | null} stored
 * @returns {string | null} the day id to activate
 *
 * Precedence:
 * - Conference day: a deliberate choice made *today* wins; otherwise
 *   today itself (a choice stored on a previous day is stale).
 * - Any other day: a valid stored choice wins; otherwise the first day.
 */
export function resolveActiveDay({ days, today, stored }) {
  if (!days || days.length === 0) return null;
  if (days.includes(today)) {
    if (stored && stored.savedOn === today && days.includes(stored.dayId)) {
      return stored.dayId;
    }
    return today;
  }
  if (stored && days.includes(stored.dayId)) {
    return stored.dayId;
  }
  return days[0];
}

/**
 * Decide how a slot is laid out in the day grid.
 *
 * The backend assigns `room-1 / room-all` to every Keynote (plenary
 * assumption) and to slots without a room. For a room-all slot that HAS
 * an assigned room we render it only in that room's column and report
 * the remaining columns as blocked (rendered grayed-out: nothing happens
 * there during the plenary). Room-all slots without a room (breaks,
 * registration) keep the full span.
 *
 * @param {object} slot - schedule slot (gridColumn, room)
 * @param {Array<[string, string]>} dayRooms - [token, label] pairs in
 *   grid column order (without the `_all_` entry)
 * @returns {{gridColumn: string, roomClasses: string, blockedColumns: string[]}}
 */
export function planRoomAllSlot(slot, dayRooms) {
  const gridColumn = slot?.gridColumn ?? '';
  const roomClasses = gridColumn
    .split('/')
    .map((room) => room.trim())
    .join(' ');
  if (!gridColumn.includes('room-all') || !slot?.room?.length) {
    return { gridColumn, roomClasses, blockedColumns: [] };
  }
  const tokens = dayRooms.map((room) => room[0]);
  const index = tokens.indexOf(slot.room[0]?.token);
  const column = `room-${index >= 0 ? index + 1 : 1}`;
  const blockedColumns = tokens
    .map((_, i) => `room-${i + 1}`)
    .filter((room) => room !== column);
  return { gridColumn: column, roomClasses: column, blockedColumns };
}

/**
 * Find the slot to scroll to: the first slot that has not started yet
 * (the *next* talk — its title is what the visitor needs to see). If
 * every slot has started, fall back to the one currently running.
 * Returns -1 when the day is over or input is invalid.
 *
 * @param {Array<{start: string|null, end: string|null}>} slots
 * @param {Date} now
 * @returns {number} index into slots, or -1
 */
export function findUpcomingSlotIndex(slots, now) {
  if (!Array.isArray(slots)) return -1;
  const nextIndex = slots.findIndex(
    (slot) => slot?.start && new Date(slot.start) > now,
  );
  if (nextIndex !== -1) return nextIndex;
  return slots.findIndex(
    (slot) =>
      slot?.start &&
      slot?.end &&
      new Date(slot.start) <= now &&
      now < new Date(slot.end),
  );
}
